/**
 * Tailwind CSS implementation of Collapsible component
 * Uses Seeds collapsible CSS classes from collapsible.css
 *
 * Only the Panel carries styling (overflow + slide keyframes), now expressed via
 * the seeds-collapsible-content class instead of styled-components. Open/closed
 * state continues to be driven by Radix's data-state attribute.
 */

import * as React from "react";
import * as RadixCollapsible from "@radix-ui/react-collapsible";
import type {
  TypeCollapsibleProps,
  TypeCollapsiblePanelProps,
  TypeCollapsibleTriggerProps,
} from "./CollapsibleTypes";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

const Collapsible = ({
  children,
  isOpen = false,
  disabled,
  defaultOpen,
  onOpenChange,
}: TypeCollapsibleProps) => {
  return (
    <RadixCollapsible.Root
      open={isOpen}
      disabled={disabled}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {children}
    </RadixCollapsible.Root>
  );
};

const Trigger = ({ children }: TypeCollapsibleTriggerProps) => {
  return (
    <RadixCollapsible.Trigger asChild>{children}</RadixCollapsible.Trigger>
  );
};

Trigger.displayName = "Collapsible.Trigger";

const Panel = ({
  children,
  forceMount,
  className,
}: TypeCollapsiblePanelProps) => {
  return (
    <RadixCollapsible.Content
      className={cn("seeds-collapsible-content", className)}
      forceMount={forceMount}
      data-qa-collapsible=""
    >
      {children}
    </RadixCollapsible.Content>
  );
};

Panel.displayName = "Collapsible.Panel";

Collapsible.Trigger = Trigger;
Collapsible.Panel = Panel;

export default Collapsible;
